import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Package, Tags, RefreshCw, Cloud, CloudOff, Clock, AlertCircle, CheckCircle2, Loader2, LayoutGrid, Save, MessageSquare, SlidersHorizontal } from 'lucide-react'
import { CrudWorkspace } from '@/components/shared/crud-workspace'
import { useFirestoreCrud } from '@/hooks'
import { omitEmptyValues, stringToBoolean, stringToList, stringToNumber } from '@/lib/cms-form'
import { ShopCategory, ShopProduct } from '@/types'
import { MetricCard } from '@/components/shared/metric-card'
import { formatCompactNumber } from '@/lib/format'
import { doc, onSnapshot, setDoc } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import { getFunctions, httpsCallable } from 'firebase/functions'

// ═══════════════════════════════════════════════════════════════════════
// JUBELIO SYNC STATUS HOOK
// ═══════════════════════════════════════════════════════════════════════

interface JubelioSyncStatus {
  token?: string
  expiresAt?: number
  lastLogin?: any
  lastSync?: any
  lastSyncStarted?: any
  syncStatus?: 'success' | 'error' | 'running'
  errorMessage?: string | null
  totalProducts?: number
  totalCategories?: number
  staleProducts?: number
}

function useJubelioSyncStatus() {
  const [status, setStatus] = useState<JubelioSyncStatus | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, '_system', 'jubelio'),
      (snapshot) => {
        if (snapshot.exists()) {
          setStatus(snapshot.data() as JubelioSyncStatus)
        } else {
          setStatus(null)
        }
        setLoading(false)
      },
      (error) => {
        console.error('[Commerce] Error listening to Jubelio sync status:', error)
        setLoading(false)
      }
    )
    return () => unsubscribe()
  }, [])

  return { status, loading }
}

function formatTimestamp(ts: any): string {
  if (!ts) return 'Belum pernah sync'
  try {
    const date = ts.toDate ? ts.toDate() : new Date(ts.seconds ? ts.seconds * 1000 : ts)
    return date.toLocaleString('id-ID', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return 'Unknown'
  }
}

// ═══════════════════════════════════════════════════════════════════════
// SYNC BUTTON COMPONENT
// ═══════════════════════════════════════════════════════════════════════

function JubelioSyncPanel({ status, statusLoading }: { status: JubelioSyncStatus | null; statusLoading: boolean }) {
  const [syncing, setSyncing] = useState(false)
  const [syncResult, setSyncResult] = useState<string | null>(null)

  const handleSync = async () => {
    setSyncing(true)
    setSyncResult(null)

    try {
      const functions = getFunctions()
      const triggerSync = httpsCallable(functions, 'triggerJubelioSync')
      const result = await triggerSync({}) as { data: { success: boolean; message: string; products: number; categories: number } }

      if (result.data.success) {
        setSyncResult(`✅ ${result.data.message}`)
      } else {
        setSyncResult('❌ Sync gagal')
      }
    } catch (e: any) {
      const msg = e?.message || 'Unknown error'
      setSyncResult(`❌ Error: ${msg}`)
    } finally {
      setSyncing(false)
    }
  }

  const isRunning = syncing || status?.syncStatus === 'running'
  const isError = status?.syncStatus === 'error'
  const isSuccess = status?.syncStatus === 'success'

  return (
    <div className="rounded-2xl border border-stone-800 bg-stone-900/50 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
            isRunning ? 'bg-blue-500/10 border border-blue-500/20' :
            isError ? 'bg-rose-500/10 border border-rose-500/20' :
            isSuccess ? 'bg-emerald-500/10 border border-emerald-500/20' :
            'bg-stone-800 border border-stone-700'
          }`}>
            {isRunning ? (
              <Loader2 className="h-5 w-5 text-blue-400 animate-spin" />
            ) : isError ? (
              <CloudOff className="h-5 w-5 text-rose-400" />
            ) : isSuccess ? (
              <Cloud className="h-5 w-5 text-emerald-400" />
            ) : (
              <Cloud className="h-5 w-5 text-stone-500" />
            )}
          </div>
          <div>
            <h3 className="text-sm font-semibold text-white">Jubelio Sync</h3>
            <p className="text-xs text-stone-500">
              {statusLoading ? 'Memuat status...' :
               isRunning ? 'Sedang sinkronisasi...' :
               status ? `Last sync: ${formatTimestamp(status.lastSync)}` :
               'Belum pernah sync'}
            </p>
          </div>
        </div>

        <button
          onClick={() => void handleSync()}
          disabled={isRunning}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
            isRunning
              ? 'bg-stone-800 text-stone-500 cursor-not-allowed'
              : 'bg-orange-500/10 text-orange-400 border border-orange-500/20 hover:bg-orange-500/20 active:scale-95'
          }`}
        >
          <RefreshCw className={`h-4 w-4 ${isRunning ? 'animate-spin' : ''}`} />
          {isRunning ? 'Syncing...' : 'Sync Sekarang'}
        </button>
      </div>

      {/* Status details */}
      {status && !statusLoading && (
        <div className="flex flex-wrap gap-3 pt-2 border-t border-stone-800/50">
          <div className="flex items-center gap-1.5 text-xs text-stone-500">
            <Package className="h-3.5 w-3.5" />
            <span>{status.totalProducts ?? '—'} produk</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-stone-500">
            <Tags className="h-3.5 w-3.5" />
            <span>{status.totalCategories ?? '—'} kategori</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-stone-500">
            <Clock className="h-3.5 w-3.5" />
            <span>Auto-sync tiap 6 jam</span>
          </div>
          {isSuccess && (
            <div className="flex items-center gap-1.5 text-xs text-emerald-400">
              <CheckCircle2 className="h-3.5 w-3.5" />
              <span>OK</span>
            </div>
          )}
        </div>
      )}

      {/* Error message */}
      {isError && status?.errorMessage && (
        <div className="flex items-start gap-2 p-2.5 rounded-xl bg-rose-500/5 border border-rose-500/10">
          <AlertCircle className="h-4 w-4 text-rose-400 mt-0.5 shrink-0" />
          <p className="text-xs text-rose-300">{status.errorMessage}</p>
        </div>
      )}

      {/* Sync result feedback */}
      {syncResult && (
        <p className={`text-xs px-2 py-1.5 rounded-lg ${
          syncResult.startsWith('✅') ? 'bg-emerald-500/10 text-emerald-300' : 'bg-rose-500/10 text-rose-300'
        }`}>
          {syncResult}
        </p>
      )}
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// CATEGORY DISPLAY SETTINGS PANEL
// Fitur untuk memilih kategori mana yang ditampilkan di halaman Store app
// ═══════════════════════════════════════════════════════════════════════

function CategoryDisplayPanel({ allCategories }: { allCategories: ShopCategory[] }) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saveResult, setSaveResult] = useState<string | null>(null)

  // Load config yang tersimpan dari Firestore
  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, '_system', 'shopConfig'),
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data()
          const ids: string[] = data.displayedCategoryIds ?? []
          setSelectedIds(new Set(ids))
        }
        setLoading(false)
      },
      () => setLoading(false)
    )
    return () => unsubscribe()
  }, [])

  // Urutkan A-Z
  const sortedCategories = [...allCategories].sort((a, b) =>
    (a.name ?? '').localeCompare(b.name ?? '', 'id')
  )

  const toggleCategory = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
    setSaveResult(null)
  }

  const selectAll = () => {
    setSelectedIds(new Set(sortedCategories.map(c => c.id!).filter(Boolean)))
    setSaveResult(null)
  }

  const clearAll = () => {
    setSelectedIds(new Set())
    setSaveResult(null)
  }

  const handleSave = async () => {
    setSaving(true)
    setSaveResult(null)
    try {
      await setDoc(
        doc(db, '_system', 'shopConfig'),
        {
          displayedCategoryIds: [...selectedIds],
          updatedAt: new Date(),
        },
        { merge: true }
      )
      setSaveResult('✅ Pengaturan kategori berhasil disimpan.')
    } catch (e: any) {
      setSaveResult(`❌ Gagal menyimpan: ${e?.message ?? 'Unknown error'}`)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10 text-stone-500 text-sm gap-2">
        <Loader2 className="h-4 w-4 animate-spin" />
        Memuat pengaturan...
      </div>
    )
  }

  if (sortedCategories.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center gap-3">
        <Tags className="h-10 w-10 text-stone-700" />
        <p className="text-stone-500 text-sm">Belum ada kategori tersinkron.</p>
        <p className="text-stone-600 text-xs">Klik "Sync Sekarang" di atas untuk mengambil kategori dari Jubelio.</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header info */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm text-stone-400">
            Pilih kategori yang akan tampil di halaman <span className="text-orange-400 font-medium">Store → Categories</span> pada aplikasi mobile.
          </p>
          <p className="text-xs text-stone-600 mt-1">
            {selectedIds.size} dari {sortedCategories.length} kategori dipilih
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <button
            onClick={clearAll}
            className="text-xs px-3 py-1.5 rounded-lg border border-stone-700 text-stone-400 hover:text-stone-200 hover:border-stone-600 transition"
          >
            Hapus Semua
          </button>
          <button
            onClick={selectAll}
            className="text-xs px-3 py-1.5 rounded-lg border border-orange-500/30 text-orange-400 hover:bg-orange-500/10 transition"
          >
            Pilih Semua
          </button>
        </div>
      </div>

      {/* Category checklist — sorted A-Z */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {sortedCategories.map((cat) => {
          const id = cat.id!
          const isChecked = selectedIds.has(id)
          return (
            <button
              key={id}
              onClick={() => toggleCategory(id)}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border text-left transition-all ${
                isChecked
                  ? 'border-orange-500/40 bg-orange-500/8 text-white'
                  : 'border-stone-800 bg-stone-900/30 text-stone-400 hover:border-stone-700 hover:text-stone-300'
              }`}
            >
              {/* Checkbox visual */}
              <span className={`h-4 w-4 shrink-0 rounded border flex items-center justify-center transition-colors ${
                isChecked ? 'border-orange-500 bg-orange-500' : 'border-stone-600 bg-transparent'
              }`}>
                {isChecked && (
                  <svg className="h-2.5 w-2.5 text-white" viewBox="0 0 10 10" fill="none">
                    <path d="M2 5l2.5 2.5L8 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </span>
              <span className="text-sm truncate">{cat.name}</span>
            </button>
          )
        })}
      </div>

      {/* Save button */}
      <div className="flex items-center justify-between pt-2 border-t border-stone-800/50">
        {saveResult && (
          <p className={`text-xs px-2 py-1 rounded-lg ${
            saveResult.startsWith('✅') ? 'bg-emerald-500/10 text-emerald-300' : 'bg-rose-500/10 text-rose-300'
          }`}>
            {saveResult}
          </p>
        )}
        <div className="ml-auto">
          <button
            onClick={() => void handleSave()}
            disabled={saving}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${
              saving
                ? 'bg-stone-800 text-stone-500 cursor-not-allowed'
                : 'bg-orange-500 text-white hover:bg-orange-600 shadow-lg shadow-orange-500/20 active:scale-95'
            }`}
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {saving ? 'Menyimpan...' : 'Simpan Pengaturan'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// WHATSAPP CS CONFIG PANEL
// Simpan nomor WA dan teks default ke Firestore _system/shopConfig
// Dibaca oleh mobile app saat user klik tombol "Buy via WA"
// ═══════════════════════════════════════════════════════════════════════

function WhatsAppConfigPanel() {
  const DEFAULT_WA_NUMBER = '6282226358580'
  const DEFAULT_WA_TEXT = 'Hey there, I need help!'

  const [waNumber, setWaNumber] = useState('')
  const [waText, setWaText] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saveResult, setSaveResult] = useState<string | null>(null)

  // Load config yang sudah tersimpan
  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, '_system', 'shopConfig'),
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data()
          setWaNumber(data.waNumber ?? DEFAULT_WA_NUMBER)
          setWaText(data.waText ?? DEFAULT_WA_TEXT)
        } else {
          setWaNumber(DEFAULT_WA_NUMBER)
          setWaText(DEFAULT_WA_TEXT)
        }
        setLoading(false)
      },
      () => setLoading(false)
    )
    return () => unsubscribe()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSave = async () => {
    setSaving(true)
    setSaveResult(null)
    try {
      await setDoc(
        doc(db, '_system', 'shopConfig'),
        {
          waNumber: waNumber.trim().replace(/[^0-9]/g, ''),
          waText: waText.trim(),
          updatedAt: new Date(),
        },
        { merge: true }
      )
      setSaveResult('✅ Konfigurasi WhatsApp berhasil disimpan.')
    } catch (e: any) {
      setSaveResult(`❌ Gagal menyimpan: ${e?.message ?? 'Unknown error'}`)
    } finally {
      setSaving(false)
    }
  }

  const previewUrl = `https://api.whatsapp.com/send/?phone=${waNumber.replace(/[^0-9]/g, '')}&text=${encodeURIComponent(waText)}&type=phone_number&app_absent=0`

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10 text-stone-500 text-sm gap-2">
        <Loader2 className="h-4 w-4 animate-spin" />
        Memuat konfigurasi...
      </div>
    )
  }

  return (
    <div className="space-y-5">
      {/* Nomor WhatsApp */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-stone-300">
          Nomor WhatsApp CS
        </label>
        <p className="text-xs text-stone-500">
          Format internasional tanpa tanda +. Contoh: <code className="text-orange-400">6282226358580</code>
        </p>
        <input
          type="text"
          value={waNumber}
          onChange={(e) => { setWaNumber(e.target.value); setSaveResult(null) }}
          placeholder={DEFAULT_WA_NUMBER}
          className="w-full bg-stone-800 border border-stone-700 text-white rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-emerald-500/60 focus:ring-1 focus:ring-emerald-500/30 transition placeholder:text-stone-600"
        />
      </div>

      {/* Teks Default Chat */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-stone-300">
          Teks Default Pesan WA
        </label>
        <p className="text-xs text-stone-500">
          Teks ini akan otomatis terisi saat user membuka WhatsApp dari aplikasi.
        </p>
        <textarea
          value={waText}
          onChange={(e) => { setWaText(e.target.value); setSaveResult(null) }}
          placeholder={DEFAULT_WA_TEXT}
          rows={3}
          className="w-full bg-stone-800 border border-stone-700 text-white rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-emerald-500/60 focus:ring-1 focus:ring-emerald-500/30 transition placeholder:text-stone-600 resize-none"
        />
      </div>

      {/* Preview Link */}
      <div className="rounded-xl border border-stone-800 bg-stone-900/50 p-4 space-y-1">
        <p className="text-xs text-stone-500 font-medium">Preview URL WhatsApp</p>
        <p className="text-xs text-emerald-400 break-all font-mono">{previewUrl}</p>
      </div>

      {/* Save button */}
      <div className="flex items-center justify-between pt-2 border-t border-stone-800/50">
        {saveResult && (
          <p className={`text-xs px-2 py-1 rounded-lg ${
            saveResult.startsWith('✅') ? 'bg-emerald-500/10 text-emerald-300' : 'bg-rose-500/10 text-rose-300'
          }`}>
            {saveResult}
          </p>
        )}
        <div className="ml-auto">
          <button
            onClick={() => void handleSave()}
            disabled={saving}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${
              saving
                ? 'bg-stone-800 text-stone-500 cursor-not-allowed'
                : 'bg-emerald-600 text-white hover:bg-emerald-700 shadow-lg shadow-emerald-600/20 active:scale-95'
            }`}
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {saving ? 'Menyimpan...' : 'Simpan Konfigurasi'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// DEFAULT SORT PANEL
// Mengatur urutan tampil default produk di halaman Store pada aplikasi mobile
// ═══════════════════════════════════════════════════════════════════════

const SORT_OPTIONS = [
  { value: 'popularity', label: 'Terpopuler (Popularity)' },
  { value: 'featured', label: 'Produk Unggulan (Featured)' },
  { value: 'priceHighToLow', label: 'Harga Tinggi ke Rendah' },
  { value: 'priceLowToHigh', label: 'Harga Rendah ke Tinggi' },
  { value: 'nameAToZ', label: 'Nama A–Z' },
  { value: 'nameZToA', label: 'Nama Z–A' },
]

function DefaultSortPanel() {
  const [selected, setSelected] = useState('priceHighToLow')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saveResult, setSaveResult] = useState<string | null>(null)

  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, '_system', 'shopConfig'),
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data()
          setSelected(data.defaultSortOption ?? 'priceHighToLow')
        }
        setLoading(false)
      },
      () => setLoading(false)
    )
    return () => unsubscribe()
  }, [])

  const handleSave = async () => {
    setSaving(true)
    setSaveResult(null)
    try {
      await setDoc(
        doc(db, '_system', 'shopConfig'),
        { defaultSortOption: selected, updatedAt: new Date() },
        { merge: true }
      )
      setSaveResult('✅ Urutan default berhasil disimpan.')
    } catch (e: any) {
      setSaveResult(`❌ Gagal menyimpan: ${e?.message ?? 'Unknown error'}`)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10 text-stone-500 text-sm gap-2">
        <Loader2 className="h-4 w-4 animate-spin" />
        Memuat konfigurasi...
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div>
        <p className="text-sm text-stone-400">
          Urutan tampil default produk saat pengguna pertama kali membuka halaman <span className="text-orange-400 font-medium">Store</span> di aplikasi mobile.
        </p>
        <p className="text-xs text-stone-600 mt-1">Pilihan ini dapat diubah pengguna kapan saja melalui tombol filter di aplikasi.</p>
      </div>

      <div className="space-y-2">
        {SORT_OPTIONS.map((opt) => {
          const isSelected = selected === opt.value
          return (
            <button
              key={opt.value}
              onClick={() => { setSelected(opt.value); setSaveResult(null) }}
              className={`flex items-center gap-3 w-full px-4 py-3 rounded-xl border text-left transition-all ${
                isSelected
                  ? 'border-orange-500/40 bg-orange-500/8 text-white'
                  : 'border-stone-800 bg-stone-900/30 text-stone-400 hover:border-stone-700 hover:text-stone-300'
              }`}
            >
              <span className={`h-4 w-4 shrink-0 rounded-full border-2 flex items-center justify-center transition-colors ${
                isSelected ? 'border-orange-500' : 'border-stone-600'
              }`}>
                {isSelected && <span className="h-2 w-2 rounded-full bg-orange-500" />}
              </span>
              <span className="text-sm">{opt.label}</span>
            </button>
          )
        })}
      </div>

      <div className="flex items-center justify-between pt-2 border-t border-stone-800/50">
        {saveResult && (
          <p className={`text-xs px-2 py-1 rounded-lg ${
            saveResult.startsWith('✅') ? 'bg-emerald-500/10 text-emerald-300' : 'bg-rose-500/10 text-rose-300'
          }`}>
            {saveResult}
          </p>
        )}
        <div className="ml-auto">
          <button
            onClick={() => void handleSave()}
            disabled={saving}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${
              saving
                ? 'bg-stone-800 text-stone-500 cursor-not-allowed'
                : 'bg-orange-500 text-white hover:bg-orange-600 shadow-lg shadow-orange-500/20 active:scale-95'
            }`}
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {saving ? 'Menyimpan...' : 'Simpan Pengaturan'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// WATCH CATEGORY PANEL
// Memilih kategori yang dianggap "jam" untuk filter popularity/featured
// ═══════════════════════════════════════════════════════════════════════

function WatchCategoryPanel({ allCategories }: { allCategories: ShopCategory[] }) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saveResult, setSaveResult] = useState<string | null>(null)

  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, '_system', 'shopConfig'),
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data()
          const ids: string[] = data.sortWatchCategoryIds ?? []
          setSelectedIds(new Set(ids))
        }
        setLoading(false)
      },
      () => setLoading(false)
    )
    return () => unsubscribe()
  }, [])

  const sortedCategories = [...allCategories].sort((a, b) =>
    (a.name ?? '').localeCompare(b.name ?? '', 'id')
  )

  const toggleCategory = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) { next.delete(id) } else { next.add(id) }
      return next
    })
    setSaveResult(null)
  }

  const selectAll = () => {
    setSelectedIds(new Set(sortedCategories.map(c => c.id!).filter(Boolean)))
    setSaveResult(null)
  }

  const clearAll = () => { setSelectedIds(new Set()); setSaveResult(null) }

  const handleSave = async () => {
    setSaving(true)
    setSaveResult(null)
    try {
      await setDoc(
        doc(db, '_system', 'shopConfig'),
        { sortWatchCategoryIds: [...selectedIds], updatedAt: new Date() },
        { merge: true }
      )
      setSaveResult('✅ Kategori jam berhasil disimpan.')
    } catch (e: any) {
      setSaveResult(`❌ Gagal menyimpan: ${e?.message ?? 'Unknown error'}`)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10 text-stone-500 text-sm gap-2">
        <Loader2 className="h-4 w-4 animate-spin" />
        Memuat pengaturan...
      </div>
    )
  }

  if (sortedCategories.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center gap-3">
        <Tags className="h-10 w-10 text-stone-700" />
        <p className="text-stone-500 text-sm">Belum ada kategori tersinkron.</p>
        <p className="text-stone-600 text-xs">Klik "Sync Sekarang" di atas untuk mengambil kategori dari Jubelio.</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm text-stone-400">
            Pilih kategori yang dianggap <span className="text-orange-400 font-medium">produk jam</span>. Saat urutan <span className="text-white font-medium">Terpopuler</span> atau <span className="text-white font-medium">Produk Unggulan</span> aktif pada tampilan <span className="text-white font-medium">Semua Produk</span>, hanya kategori ini yang ditampilkan.
          </p>
          <p className="text-xs text-stone-600 mt-1">
            {selectedIds.size} dari {sortedCategories.length} kategori dipilih
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <button
            onClick={clearAll}
            className="text-xs px-3 py-1.5 rounded-lg border border-stone-700 text-stone-400 hover:text-stone-200 hover:border-stone-600 transition"
          >
            Hapus Semua
          </button>
          <button
            onClick={selectAll}
            className="text-xs px-3 py-1.5 rounded-lg border border-orange-500/30 text-orange-400 hover:bg-orange-500/10 transition"
          >
            Pilih Semua
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {sortedCategories.map((cat) => {
          const id = cat.id!
          const isChecked = selectedIds.has(id)
          return (
            <button
              key={id}
              onClick={() => toggleCategory(id)}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border text-left transition-all ${
                isChecked
                  ? 'border-orange-500/40 bg-orange-500/8 text-white'
                  : 'border-stone-800 bg-stone-900/30 text-stone-400 hover:border-stone-700 hover:text-stone-300'
              }`}
            >
              <span className={`h-4 w-4 shrink-0 rounded border flex items-center justify-center transition-colors ${
                isChecked ? 'border-orange-500 bg-orange-500' : 'border-stone-600 bg-transparent'
              }`}>
                {isChecked && (
                  <svg className="h-2.5 w-2.5 text-white" viewBox="0 0 10 10" fill="none">
                    <path d="M2 5l2.5 2.5L8 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </span>
              <span className="text-sm truncate">{cat.name}</span>
            </button>
          )
        })}
      </div>

      <div className="flex items-center justify-between pt-2 border-t border-stone-800/50">
        {saveResult && (
          <p className={`text-xs px-2 py-1 rounded-lg ${
            saveResult.startsWith('✅') ? 'bg-emerald-500/10 text-emerald-300' : 'bg-rose-500/10 text-rose-300'
          }`}>
            {saveResult}
          </p>
        )}
        <div className="ml-auto">
          <button
            onClick={() => void handleSave()}
            disabled={saving}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${
              saving
                ? 'bg-stone-800 text-stone-500 cursor-not-allowed'
                : 'bg-orange-500 text-white hover:bg-orange-600 shadow-lg shadow-orange-500/20 active:scale-95'
            }`}
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {saving ? 'Menyimpan...' : 'Simpan Pengaturan'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// MAIN COMMERCE PAGE
// ═══════════════════════════════════════════════════════════════════════

export function CommercePage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const activeTab = searchParams.get('tab') || 'products'

  const categories = useFirestoreCrud<ShopCategory>('shopCategories')
  const products = useFirestoreCrud<ShopProduct>('shopProducts')
  const { status: syncStatus, loading: syncLoading } = useJubelioSyncStatus()

  const tabs: { id: string; label: string; icon: any }[] = [
    { id: 'products', label: 'Daftar Produk', icon: Package },
    { id: 'categories', label: 'Kategori Produk', icon: Tags },
    { id: 'display', label: 'Tampilan Kategori', icon: LayoutGrid },
    { id: 'pengurutan', label: 'Pengurutan', icon: SlidersHorizontal },
    { id: 'whatsapp', label: 'WhatsApp CS', icon: MessageSquare },
  ]

  const setActiveTab = (id: string) => {
    setSearchParams({ tab: id })
  }

  // Urutkan kategori A-Z untuk dropdown di form produk
  const sortedCategoryNames = [...categories.items]
    .sort((a, b) => ((a as any).name ?? '').localeCompare((b as any).name ?? '', 'id'))
    .map((c) => (c as any).name)
    .filter(Boolean)

  // Count Jubelio vs manual products
  const jubelioProductCount = products.items.filter((p: any) => p.jubelioSynced).length

  return (
    <div className="space-y-6">
      {/* Jubelio Sync Panel */}
      <JubelioSyncPanel status={syncStatus} statusLoading={syncLoading} />

      <div className="grid gap-4 md:grid-cols-3">
        <MetricCard
          title="Total Produk"
          value={formatCompactNumber(products.items.length)}
          description="Produk aktif di katalog shop."
          icon={Package}
        />
        <MetricCard
          title="Dari Jubelio"
          value={formatCompactNumber(jubelioProductCount)}
          description="Produk tersinkron dari Jubelio WMS."
          icon={Cloud}
          tone="emerald"
        />
        <MetricCard
          title="Kategori Produk"
          value={formatCompactNumber(categories.items.length)}
          description="Pengelompokan jenis produk."
          icon={Tags}
          tone="amber"
        />
      </div>

      <div className="flex flex-wrap gap-2 border-b border-stone-800 pb-1">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition ${
              activeTab === tab.id
                ? 'border-b-2 border-orange-500 text-orange-500'
                : 'text-stone-500 hover:text-stone-300'
            }`}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'products' && (
        <CrudWorkspace<ShopProduct>
          title="Produk"
          description="Kelola daftar produk yang tampil di tab Shop. Produk otomatis tersinkron dari Jubelio WMS setiap 6 jam."
          emptyTitle="Produk kosong"
          emptyDescription="Belum ada produk di katalog. Klik 'Sync Sekarang' di atas untuk mengambil data dari Jubelio."
          enableSearchAndPagination
          searchPlaceholder="Cari produk, kategori, atau slug..."
          fields={[
            { key: 'image', label: 'Foto Produk', type: 'image' },
            { key: 'name', label: 'Nama Produk', placeholder: 'Parlent Palladium Silver' },
            { key: 'slug', label: 'Slug Produk', placeholder: 'parlent-palladium-silver' },
            {
              key: 'category',
              label: 'Kategori',
              type: 'select',
              // Diurutkan A-Z
              options: sortedCategoryNames,
            },
            { key: 'price', label: 'Harga Tampilan', placeholder: 'Rp 1.000.000' },
            { key: 'priceValue', label: 'Harga Numerik (Sorting)', type: 'number', placeholder: '1000000' },
            { key: 'oldPrice', label: 'Harga Coret (Optional)', placeholder: 'Rp 1.500.000' },
            { key: 'stock', label: 'Stok Terlihat', type: 'number', placeholder: '99' },
            { key: 'tagline', label: 'Tagline Singkat', placeholder: 'Luxury meets performance.' },
            { key: 'tokopediaUrl', label: 'Link Tokopedia', placeholder: 'https://tokopedia.com/...' },
            { key: 'shopeeUrl', label: 'Link Shopee', placeholder: 'https://shopee.co.id/...' },
            { key: 'tiktokShopUrl', label: 'Link TikTok Shop', placeholder: 'https://shop.tiktok.com/...' },
            { key: 'description', label: 'Deskripsi Produk', type: 'richtext' },
            { key: 'galleryImages', label: 'Galeri Tambahan', type: 'textarea', placeholder: 'Satu URL gambar per baris' },
            { key: 'highlights', label: 'Highlights', type: 'textarea', placeholder: 'Satu highlight per baris' },
            {
              key: 'status',
              label: 'Status',
              type: 'select',
              options: ['active', 'inactive', 'out_of_stock'],
            },
            { key: 'favorite', label: 'Rekomendasi Utama', type: 'boolean' },
            {
              key: 'priority',
              label: 'Prioritas Unggulan',
              type: 'number',
              placeholder: '0 = tidak diset, 1 = paling depan',
            },
          ]}
          items={products.items}
          onSave={(data) => {
            const payload = omitEmptyValues({
              id: data.id,
              image: data.image,
              name: data.name,
              slug: data.slug,
              category: data.category,
              price: data.price,
              priceValue: stringToNumber(data.priceValue),
              oldPrice: data.oldPrice,
              stock: stringToNumber(data.stock),
              tagline: data.tagline,
              tokopediaUrl: data.tokopediaUrl,
              shopeeUrl: data.shopeeUrl,
              tiktokShopUrl: data.tiktokShopUrl,
              description: data.description,
              galleryImages: stringToList(data.galleryImages),
              highlights: stringToList(data.highlights),
              status: data.status || 'draft',
              favorite: stringToBoolean(data.favorite),
              // priority: manual admin field, TIDAK di-overwrite oleh Jubelio sync
              priority: stringToNumber(data.priority),
            })
            void products.upsert(payload)
          }}
          onDelete={products.remove}
        />
      )}

      {activeTab === 'categories' && (
        <CrudWorkspace<ShopCategory>
          title="Kategori Produk"
          description="Kategori otomatis tersinkron dari Jubelio. Anda juga bisa menambah kategori manual."
          emptyTitle="Kategori kosong"
          emptyDescription="Belum ada kategori produk. Klik 'Sync Sekarang' di atas untuk mengambil data dari Jubelio."
          fields={[
            { key: 'image', label: 'Icon/Cover Kategori', type: 'image' },
            { key: 'name', label: 'Nama Kategori', placeholder: 'Automatic / Quartz / Active' },
            { key: 'slug', label: 'Slug Kategori', placeholder: 'automatic' },
            { key: 'description', label: 'Deskripsi', type: 'textarea', placeholder: 'Dipakai untuk navigasi kategori produk.' },
            { key: 'index', label: 'Urutan Tampil', type: 'number', placeholder: '1' },
            {
              key: 'status',
              label: 'Status',
              type: 'select',
              options: ['active', 'inactive'],
            },
          ]}
          items={[...categories.items].sort((a, b) =>
            ((a as any).name ?? '').localeCompare((b as any).name ?? '', 'id')
          )}
          onSave={(data) => {
            const payload = omitEmptyValues({
              id: data.id,
              image: data.image,
              name: data.name,
              slug: data.slug,
              description: data.description,
              index: stringToNumber(data.index),
              status: data.status || 'active',
            })
            void categories.upsert(payload)
          }}
          onDelete={categories.remove}
        />
      )}

      {activeTab === 'display' && (
        <div className="rounded-2xl border border-stone-800 bg-stone-900/30 p-5">
          <div className="mb-4">
            <h2 className="text-base font-semibold text-white flex items-center gap-2">
              <LayoutGrid className="h-4 w-4 text-orange-400" />
              Pengaturan Tampilan Kategori
            </h2>
            <p className="text-xs text-stone-500 mt-1">
              Tentukan kategori mana yang muncul di bagian <strong className="text-stone-400">Categories</strong> pada halaman Store di aplikasi mobile.
              Perubahan langsung aktif setelah disimpan.
            </p>
          </div>
          <CategoryDisplayPanel allCategories={categories.items} />
        </div>
      )}

      {activeTab === 'pengurutan' && (
        <div className="space-y-5">
          <div className="rounded-2xl border border-stone-800 bg-stone-900/30 p-5">
            <div className="mb-4">
              <h2 className="text-base font-semibold text-white flex items-center gap-2">
                <SlidersHorizontal className="h-4 w-4 text-orange-400" />
                Urutan Tampil Default
              </h2>
              <p className="text-xs text-stone-500 mt-1">
                Urutan yang aktif saat pengguna pertama kali membuka halaman Store di aplikasi mobile.
              </p>
            </div>
            <DefaultSortPanel />
          </div>

          <div className="rounded-2xl border border-stone-800 bg-stone-900/30 p-5">
            <div className="mb-4">
              <h2 className="text-base font-semibold text-white flex items-center gap-2">
                <Tags className="h-4 w-4 text-orange-400" />
                Kategori Jam (Filter Popularity & Featured)
              </h2>
              <p className="text-xs text-stone-500 mt-1">
                Saat urutan <strong className="text-stone-300">Terpopuler</strong> atau <strong className="text-stone-300">Produk Unggulan</strong> aktif pada tampilan <strong className="text-stone-300">Semua Produk</strong>, hanya kategori yang dipilih di sini yang akan ditampilkan. Perubahan langsung aktif setelah disimpan.
              </p>
            </div>
            <WatchCategoryPanel allCategories={categories.items} />
          </div>
        </div>
      )}

      {activeTab === 'whatsapp' && (
        <div className="rounded-2xl border border-stone-800 bg-stone-900/30 p-5">
          <div className="mb-5">
            <h2 className="text-base font-semibold text-white flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-emerald-400" />
              Konfigurasi WhatsApp CS
            </h2>
            <p className="text-xs text-stone-500 mt-1">
              Nomor dan teks default yang digunakan saat user menekan tombol{' '}
              <strong className="text-emerald-400">Buy via WA</strong> pada halaman detail produk di aplikasi mobile.
            </p>
          </div>
          <WhatsAppConfigPanel />
        </div>
      )}
    </div>
  )
}
